import React from "react";
import { Link } from "react-router-dom";
import { Utensils, MapPin, CalendarDays, Bus, Lightbulb, ImageOff, Route, ChefHat, CakeSlice, Soup, BookOpen, Sandwich } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import RecipeDialog, { VideoRecipeLink } from "@/components/RecipeDialog";
import LiveFood from "@/components/LiveFood";
import { KIND_LABEL } from "@/lib/recipe";

const pick = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";
const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function TravelGuide({ guide, code }) {
  const { lang, t } = useLang();
  if (!guide) return null;

  return (
    <div className="space-y-5" data-testid="travel-guide">
      <Row icon={Utensils} label={t("guide_dishes")} testid="guide-dishes">
        <div className="flex flex-wrap gap-2">
          {guide.dishes.map((d, i) => (
            <span key={d.en} className="text-sm px-3 py-1 rounded-full bg-ochre/10 text-ochre border border-ochre/25" data-testid={`guide-dish-${i}`}>
              {pick(d, lang)}
            </span>
          ))}
        </div>
      </Row>

      {guide.cuisine && Object.keys(guide.cuisine).length > 0 && (
        <Row icon={ChefHat} label={t("guide_cuisine")} testid="guide-cuisine">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["dish", "dessert", "street"].map((kind) => guide.cuisine[kind] && <FoodCard key={kind} kind={kind} food={guide.cuisine[kind]} lang={lang} t={t} code={code} />)}
          </div>
          <Link to="/ricettario" className="inline-flex items-center gap-1.5 mt-2 text-xs text-ochre hover:underline" data-testid="guide-cookbook-link"><BookOpen className="w-3.5 h-3.5" /> {t("cookbook_cta")}</Link>
        </Row>
      )}

      <Row icon={MapPin} label={t("guide_places")} testid="guide-places">
        <div className="grid grid-cols-2 gap-3">
          {guide.places.map((p, i) => (
            <PlaceCard key={p.wiki} place={p} lang={lang} index={i} />
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">{t("photo_wiki")}</div>
      </Row>

      <Row icon={CalendarDays} label={t("guide_best_time")} testid="guide-best-time">
        <p className="text-sm text-ink">{pick(guide.best_time, lang)}</p>
      </Row>

      <Row icon={Bus} label={t("guide_getting_around")} testid="guide-getting-around">
        <p className="text-sm leading-relaxed text-ink/85">{pick(guide.getting_around, lang)}</p>
      </Row>

      {guide.itinerary?.length > 0 && (
        <Row icon={Route} label={t("guide_itinerary")} testid="guide-itinerary">
          <ol className="relative border-s-2 border-ochre/30 ms-3 space-y-3">
            {guide.itinerary.map((d, i) => (
              <li key={d.en} className="ps-5 relative" data-testid={`guide-day-${i + 1}`}>
                <span className="absolute -start-[11px] top-0.5 w-5 h-5 rounded-full bg-ochre text-white text-[10px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("day")} {i + 1}</div>
                <p className="text-sm text-ink leading-snug">{pick(d, lang)}</p>
              </li>
            ))}
          </ol>
        </Row>
      )}

      <div className="rounded-xl bg-oasis/8 border border-oasis/25 p-3 flex gap-2.5" data-testid="guide-tip">
        <Lightbulb className="w-4 h-4 text-oasis shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-oasis mb-0.5">{t("guide_tip")}</div>
          <p className="text-sm leading-relaxed text-ink/85">{pick(guide.tip, lang)}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      {children}
    </div>
  );
}

function PlaceCard({ place, lang, index }) {
  const name = pick(place.name, lang);
  return (
    <a
      href={`https://en.wikipedia.org/wiki/${encodeURIComponent(place.wiki.replace(/ /g, "_"))}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group rounded-xl overflow-hidden border border-stoneLine bg-sand/40 hover:border-ochre/50 transition-colors"
      data-testid={`guide-place-${index}`}
    >
      <div className="aspect-[4/3] bg-sandDeep/40 overflow-hidden flex items-center justify-center">
        {place.image ? (
          <img
            src={imgSrc(place.image)}
            alt={name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <ImageOff className="w-6 h-6 text-muted-foreground/50" />
        )}
      </div>
      <div className="p-2.5">
        <div className="text-sm font-semibold text-ink leading-tight">{name}</div>
        <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">{pick(place.desc, lang)}</p>
      </div>
    </a>
  );
}

function FoodCard({ kind, food, lang, t, code }) {
  const name = pick(food.name, lang);
  const Icon = kind === "dessert" ? CakeSlice : kind === "street" ? Sandwich : Soup;
  const ingredients = food.ingredients?.[lang] || food.ingredients?.en || [];
  return (
    <article className="rounded-xl overflow-hidden border border-stoneLine bg-sand/40" data-testid={`guide-food-${kind}`}>
      {food.image ? (
        <LiveFood food={food} kind={kind} alt={name} className="aspect-[16/10]">
          <span className="absolute top-2 start-2 z-10 inline-flex items-center gap-1 rounded-full bg-ink/80 text-sand text-[10px] font-semibold px-2 py-0.5 backdrop-blur-sm"><Icon className="w-3 h-3" /> {t(KIND_LABEL[kind])}</span>
        </LiveFood>
      ) : (
        <div className="aspect-[16/10] bg-sandDeep/40 flex items-center justify-center"><ImageOff className="w-6 h-6 text-muted-foreground/50" /></div>
      )}
      <div className="p-3">
        <div className="text-sm font-semibold text-ink leading-tight" data-testid={`guide-food-${kind}-name`}>{name}</div>
        <p className="text-xs text-muted-foreground mt-1 leading-snug">{pick(food.desc, lang)}</p>
        <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{t("guide_ingredients")}</div>
        <ul className="mt-1 flex flex-wrap gap-1">
          {ingredients.map((ing) => <li key={ing} className="text-[11px] px-2 py-0.5 rounded-full bg-card border border-stoneLine text-ink">{ing}</li>)}
        </ul>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <RecipeDialog food={food} code={code} kind={kind} />
          <VideoRecipeLink food={food} code={code} kind={kind} className="text-xs px-3 py-1.5" />
        </div>
      </div>
    </article>
  );
}
